import './App.css';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams } from 'react-router-dom';

import { User as LocalUser } from './interfaces/user.interface';

import { EventManagerService } from './services/eventManagers.service';
import { isTableAdmin } from './services/right.service';

import { clearToken, clearUser, CommonAuthService, RootState, saveToken, saveUser, UserUmanforge, clearVisualisationToken, LoggerService, CommonGlobalService, getAppsFromMask, APPS_NAME, configService } from '@umanforge/common';

import { Loading } from '@common_components/loading.component';

// Pages
import Home from './views/home.view';
import ExtractionView from './views/extraction.view';
import ProfilView from './views/profil.view';
import OnlineHelpView from './views/onlineHelp.view';
import EventManagerView from './views/eventManager.view';
import IndicatorsView from './views/indicators.view';
import DiaryNoteView from './views/diaryNote.view';
import ParametersView from 'views/settings.view';
import GalleryView from '_development/gallery.view';
import VisualisationEventManagerView from 'views/visualisationEventManager.view';

import { KEY } from 'constants/local_storage_key.constant';
import { VERSION } from 'constants/app.constants';
import { EventService } from 'services/events.service';

/**
 * Composant wrapper pour gérer la redirection vers EventManager avec paramètres
 * Vérifie que l'utilisateur a le droit d'accéder à cet EventManager
 * L'utilisateur doit être:
 * - Participant du tableau, OU
 * - Admin du tableau, OU
 * - Admin du secteur du tableau, OU
 * - Admin du site du tableau, OU
 * - Admin du client du tableau
 */
function EventManagerRedirectWrapper() {
  const { clientId, siteId, sectorId, eventManagerId, eventId, engagementId } = useParams<{ clientId: string; siteId: string; sectorId: string; eventManagerId: string; eventId: string; engagementId?: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const me = useSelector( ( state: RootState ) => { return state.user?.value; } );
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const [ isChecking, setIsChecking ] = useState( true );

  useEffect( () => {
    const checkAccessRights = async () => {

      if ( !me || !token ) {
        navigate( '/EventManager', { replace: true } );
        return;
      }

      // Vérifier que les paramètres sont présents
      if ( !eventManagerId || !clientId || !siteId || !sectorId || !eventId ) {
        navigate( '/EventManager', { replace: true } );
        return;
      }

      try {
        // Récupérer les infos de l'EventManager
        const eventManager = await EventManagerService.getEventManagerById( eventManagerId, token, dispatch );
        if ( !eventManager ) {
          navigate( '/EventManager', { replace: true } );
          return;
        }

        // Récupérer les infos de l'Event
        const event = await EventService.getEventById( eventId, token, dispatch );
        if ( !event ) {
          navigate( '/EventManager', { replace: true } );
          return;
        }

        const selectedEngagementId = engagementId ?? event.engagements.map( eng => { return eng.id; } )[ 0 ];
        if ( !selectedEngagementId ) {
          navigate( '/EventManager', { replace: true } );
          return;
        }

        // Vérifier les droits d'accès
        let hasAccess = false;

        // 1. Vérifier si l'utilisateur est participant ou admin du tableau
        if ( eventManager.participants && Array.isArray( eventManager.participants ) ) {
          // Vérifier si participant
          const isParticipant = eventManager.participants.some( ( p: LocalUser & { eventManagersId?: string[] } ) => {
            return p.id === me.id;
          } );
          hasAccess = hasAccess || isParticipant;

          // Vérifier si admin du tableau
          hasAccess = hasAccess || isTableAdmin(
            eventManager.participants.map( ( p: LocalUser & { eventManagersId?: string[] } ) => {return  { userId: p.id, isAdmin: false }; } ),
            me.id
          );
        }

        // 2. Vérifier si admin du secteur du tableau
        if ( eventManager.sectors && Array.isArray( eventManager.sectors ) ) {
          for ( const sectorIdOfEventManager of eventManager.sectors ) {
            if ( CommonGlobalService.isAdminOfSector( me, sectorIdOfEventManager ) ) {
              hasAccess = true;
              break;
            }
          }
        }

        // 3. Vérifier si admin du site du tableau
        if ( eventManager.site && Array.isArray( eventManager.site ) && me.clients ) {
          for ( const eventSite of eventManager.site ) {
            for ( const client of me.clients ) {
              if ( client.sites ) {
                for ( const site of client.sites ) {
                  // eslint-disable-next-line max-depth
                  if ( site.id === eventSite.id && site.isAdmin ) {
                    hasAccess = true;
                    break;
                  }
                }
              }
            }
          }
        }

        // 5. Vérifier si super admin
        hasAccess = hasAccess || me.isSuperAdmin || ( me as unknown as Record<string, boolean> )[ 'is_super_admin' ] === true;

        if ( !hasAccess ) {
          // Pas de droits - rediriger vers EventManager
          navigate( '/EventManager', { replace: true } );
          return;
        }

        // L'utilisateur a les droits - rediriger vers EventManager avec les paramètres
        // Sauvegarder les IDs dans le localStorage pour que EventManagerView les récupère
        localStorage.setItem( KEY.SELECTED_EVENT_MANAGER_ID, eventManagerId );
        localStorage.setItem( KEY.SELECTED_CLIENT_ID, clientId );
        localStorage.setItem( KEY.SELECTED_SITE_ID, siteId );
        localStorage.setItem( KEY.SELECTED_SECTOR_ID, sectorId );
        localStorage.setItem( KEY.SELECTED_EVENT_ID, eventId );
        localStorage.setItem( KEY.SELECTED_ENGAGEMENT_ID, selectedEngagementId );
        const query = `/EventManager?clientId=${clientId}&siteId=${siteId}&sectorId=${sectorId}&eventManagerId=${eventManagerId}&eventId=${eventId}&engagementId=${selectedEngagementId}`;
        navigate( query, { replace: true } );
      } catch ( error ) {
        console.error( 'Error checking access rights:', error );
        navigate( '/EventManager', { replace: true } );
      } finally {
        setIsChecking( false );
      }
    };

    checkAccessRights();
  }, [ clientId, siteId, sectorId, eventManagerId, eventId, engagementId, navigate, me, token, dispatch ] );

  if ( isChecking ) {
    return (
      <div className='flex w-screen h-screen items-center justify-center'>
        <Loading size={200} />
      </div>
    );
  }

  return null;
}


const logger: LoggerService = new LoggerService( 'App' );

export function App() {

  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const visualisationToken = useSelector( ( state: RootState ) => { return state.persistedReducer.visualisationToken.value; } );
  const [ isCheckingAuth, setIsCheckingAuth ] = useState<boolean>( true );

  useEffect( () => {
    logger.verbose( 'Version :', VERSION );
  }, [] );

  useEffect( () => {
    const urlParams = new URLSearchParams( window.location.search );
    const umanforge = urlParams.get( 'umanforge' );
    if ( umanforge != null && umanforge != '' ) {
      window.history.pushState( {}, document.title, window.location.pathname );
    }
    CommonAuthService.isSignIn( userNotConnected )
      .then( onResponse );
  }, [ token ] );

  useEffect( () => {
    const checkUserConnection = () => {
      if ( !document.hidden ) {
        CommonAuthService.isSignIn( userNotConnected )
          .then( onResponse );
      }
    };

    document.addEventListener( 'visibilitychange', checkUserConnection );

    return () => {
      document.removeEventListener( 'visibilitychange', checkUserConnection );
    };
  }, [ visualisationToken ] );

  const onResponse = ( response: {
    signin: boolean;
    user: UserUmanforge | null;
    token: string | null;
  } ) => {
    if ( !response.signin || response.user == null || response.user.needChangePassword || !response.token ) {
      userNotConnected();
    } else {
      userConnected( response.user, response.token );
    }
  };

  const userNotConnected = () => {
    dispatch( clearToken() );
    dispatch( clearUser() );
    setIsCheckingAuth( false );
    if ( !visualisationToken ) {
      CommonGlobalService.openAuthPage();
    }
  };

  const userConnected = async ( user: UserUmanforge, newToken: string ) => {
    // Vérifier que l'utilisateur a accès à l'application Flashmeeting
    const mask = Number( ( user as unknown as Record<string, unknown> ).appAccess ?? ( user as unknown as Record<string, unknown> ).app_access ?? 15 );
    const allowedApps = getAppsFromMask( mask );
    const hasFlashAccess = allowedApps.includes( APPS_NAME.FLASHMEETING );
    if ( !hasFlashAccess ) {
      // Rediriger vers Umanforge
      const umanforgeUrl = await configService.getUmanforgeUrl();
      window.location.href = String( umanforgeUrl );
      return;
    }

    dispatch( clearVisualisationToken() );
    dispatch( saveToken( newToken as string ) );
    dispatch( saveUser( user as UserUmanforge ) );
    setIsCheckingAuth( false );
  };

  if ( isCheckingAuth ) {
    return (
      <div className='flex w-screen h-screen items-center justify-center'>
        <Loading size={ 200 } />
      </div>
    );
  }

  return (
    <div>
      {
        token || visualisationToken ?
          <Router>
            <Routes>
              <Route path="*"
                element={<Home />} />
              <Route path="/Home"
                element={<Home />} />
              <Route path="/:clientId/:siteId/:sectorId/:eventManagerId/:eventId/:engagementId"
                element={<EventManagerRedirectWrapper />} />
              <Route path="/:clientId/:siteId/:sectorId/:eventManagerId/:eventId"
                element={<EventManagerRedirectWrapper />} />
              <Route path="/EventManager"
                element={<EventManagerView />} />
              <Route path="/Extraction"
                element={<ExtractionView />} />
              <Route path="/Profil"
                element={<ProfilView />} />
              <Route path="/OnlineHelp"
                element={<OnlineHelpView />} />
              <Route path="/DiaryNote"
                element={<DiaryNoteView />} />
              <Route path="/Indicators"
                element={<IndicatorsView />} />
              <Route path="/Parameters"
                element={<ParametersView />} />
              <Route path="/dev/Gallery"
                element={<GalleryView />} />
              <Route path="/Visualisation/EventManagers"
                element={<VisualisationEventManagerView />} />
            </Routes>
          </Router>
          :
          null
      }
    </div>
  );
}